import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Fingerprint } from "lucide-react";
import useApi from "../../../CustomHooks/useApi";
import AadhaarPayKycComponent from "./AadhaarPayKycComponent";
import AadhaarPayTwoFactorAuth from "./AadhaarPayTwoFactorAuth";
import localBanksData from "../../../MasterJson/banks_aeps_2.json";

const BANK_LABELS = [
    { short: "SBI",      full: "State Bank of India" },
    { short: "PNB",      full: "Punjab National Bank" },
    { short: "BOB",      full: "BANK OF BARODA" },
    { short: "BOI",      full: "BANK OF INDIA" },
    { short: "UBI",      full: "UNION BANK OF INDIA" },
    { short: "INDIAN",   full: "INDIAN BANK" },
    { short: "CBI",      full: "CENTRAL BANK OF INDIA" },
    { short: "CANARA",   full: "CANARA BANK" },
    { short: "BOM",      full: "BANK OF MAHARASHTRA" },
    { short: "PSB",      full: "PUNJAB AND SIND BANK" },
    { short: "IOB",      full: "INDIAN OVERSEAS BANK" },
    { short: "UCO",      full: "UCO BANK" },
    { short: "AIRTEL",   full: "AIRTEL PAYMENTS BANK" },
    { short: "IPPB",     full: "INDIA POST PAYMENTS BANK" },
    { short: "HDFC",     full: "HDFC Bank" },
    { short: "ICICI",    full: "ICICI Bank" },
    { short: "AXIS",     full: "Axis Bank" },
    { short: "IDBI",     full: "IDBI BANK" },
    { short: "INDUSIND", full: "IndusInd Bank" },
    { short: "KOTAK",    full: "Kotak Mahindra Bank" },
];

const BANK_COLORS = [
    "bg-blue-700", "bg-red-600",    "bg-orange-600", "bg-indigo-900",
    "bg-rose-600", "bg-blue-800",   "bg-blue-600",   "bg-teal-600",
    "bg-sky-600",  "bg-orange-500", "bg-indigo-700", "bg-green-600",
    "bg-purple-600","bg-cyan-600",  "bg-red-500",    "bg-orange-700",
    "bg-pink-600", "bg-violet-600", "bg-emerald-600","bg-fuchsia-600",
];

export default function AadhaarPayForm({
    onSubmit,
    loading,
    previousData,
    processing,
    clearFormTrigger,
}) {
    const userId   = useSelector((state) => state.auth?.employeeDetails?.userId);
    const username = useSelector((state) => state.auth?.employeeDetails?.username);

    // ── Aadhaar Pay specific APIs ──
    const { callApi: checkEkycApi } = useApi(
        `/aeps/fingpay/ekyc-status/${username || ""}`,
        "GET"
    );
    const { callApi: check2FAApi } = useApi(
        `/aeps/fingpay/daily-2fa-status/${username}/AP`,
        "GET"
    );

    const [banks, setBanks]                         = useState([]);
    const [form, setForm]                           = useState({
        aadhaar: "",
        mobile:  "",
        amount:  "",
        bank:    "",
        txnType: "AP",   // "AP" = main form, "EKYC" = eKYC flow, "2FA" = 2FA flow
    });

    const [is2FAVerified, setIs2FAVerified]         = useState(true);
    const [showDaily2FAAlert, setShowDaily2FAAlert] = useState(false);
    const [showEkycRequired, setShowEkycRequired]   = useState(false);
    const [showOtherBanks, setShowOtherBanks]       = useState(false);

    // ── Input handlers ──
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "bank") {
            const selected = banks.find((b) => b.iin === value);
            setForm((prev) => ({ ...prev, bank: selected || "" }));
            return;
        }
        if (name === "aadhaar") {
            const n = value.replace(/\D/g, "");
            if (n.length <= 12) setForm((prev) => ({ ...prev, aadhaar: n }));
            return;
        }
        if (name === "mobile") {
            const n = value.replace(/\D/g, "");
            if (n.length <= 10) setForm((prev) => ({ ...prev, mobile: n }));
            return;
        }
        if (name === "amount") {
            const n = value.replace(/\D/g, "");
            setForm((prev) => ({ ...prev, amount: n }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const isFormValid =
        form.aadhaar.length === 12 &&
        form.mobile.length === 10 &&
        !!form.bank &&
        form.amount.trim() !== "" &&
        parseInt(form.amount) > 0;

    // ── Bank quick-select ──
    const handleQuickBankSelect = (bank) =>
        setForm((prev) => ({ ...prev, bank }));

    // ── Restore previous data ──
    const handlePreviousData = () => {
        if (!previousData) return;
        setForm((prev) => ({ ...prev, ...previousData, bank: previousData?.bank || "" }));
    };

    // ── Status check (eKYC → 2FA, same logic as AepsForm1) ──
    const checkStatus = async () => {
        if (!userId) return;
        try {
            // 1. eKYC first
            const ekycRes = await checkEkycApi(null);
            if (ekycRes?.ekycDone !== true) {
                setShowEkycRequired(true);
                setShowDaily2FAAlert(false);
                setIs2FAVerified(false);
                return;
            }
            setShowEkycRequired(false);

            // 2. Daily 2FA
            const twoFaRes = await check2FAApi(null);
            if (twoFaRes?.authenticated === true) {
                setIs2FAVerified(true);
                setShowDaily2FAAlert(false);
            } else {
                setIs2FAVerified(false);
                setShowDaily2FAAlert(true);
            }
        } catch {
            setShowEkycRequired(true);
            setIs2FAVerified(false);
        }
    };

    // ── Load banks from local JSON ──
    const loadBanks = () => {
        try {
            if (localBanksData?.statuscode === "TXN") {
                setBanks(localBanksData?.data || []);
            }
        } catch (err) {
            console.error("Failed to load local bank data", err);
        }
    };

    useEffect(() => { checkStatus(); }, [userId]);
    useEffect(() => { loadBanks(); }, []);

    useEffect(() => {
        if (clearFormTrigger) {
            setForm((prev) => ({ ...prev, aadhaar: "", mobile: "", amount: "", bank: "" }));
        }
    }, [clearFormTrigger]);

    // ── Routing to sub-flows ──
    if (form.txnType === "EKYC") {
        return (
            <AadhaarPayKycComponent
                onBack={() => setForm({ ...form, txnType: "AP" })}
                onSuccess={() => {
                    checkStatus();
                    setForm((prev) => ({ ...prev, txnType: "AP" }));
                }}
            />
        );
    }

    if (form.txnType === "2FA") {
        return (
            <AadhaarPayTwoFactorAuth
                onBack={() => setForm({ ...form, txnType: "AP" })}
                onSuccess={() => {
                    checkStatus();
                    setForm((prev) => ({ ...prev, txnType: "AP" }));
                }}
            />
        );
    }

    // ── Bank grid data ──
    const highlightedBanks = BANK_LABELS.map((item) => ({
        label: item.short,
        bank:  banks.find((b) => b.name?.toLowerCase() === item.full.toLowerCase()),
    })).filter((x) => x.bank);

    const otherBanks = banks.filter(
        (b) => !BANK_LABELS.some((item) => item.full.toLowerCase() === b.name?.toLowerCase())
    );

    return (
        <div className="overflow-hidden bg-[#f4f6fb]">

            {/* Status alerts */}
            {showEkycRequired ? (
                <div className="bg-orange-50 border border-orange-300 rounded-lg text-center p-3 mb-2">
                    <p className="text-orange-700 font-semibold text-base">
                        Please complete e-KYC first. After bank approval you can perform Aadhaar Pay transactions.
                    </p>
                </div>
            ) : (
                showDaily2FAAlert && (
                    <div className="mb-2 bg-blue-50 border p-2 rounded-lg text-center text-blue-700 text-base font-medium">
                        Daily 2FA required before Aadhaar Pay transactions
                    </div>
                )
            )}

            <div className="grid grid-cols-12 h-full">

                {/* LEFT — Form */}
                <div className="col-span-5 border-r px-5 py-4 flex flex-col justify-start h-full">

                    <h3 className="font-bold text-xl mb-4">Aadhaar Pay</h3>

                    {/* Transaction type radios — only eKYC and 2FA */}
                    <div className="grid grid-cols-2 gap-y-3 text-base font-semibold mb-6">
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                checked={form.txnType === "EKYC"}
                                onChange={() => setForm({ ...form, txnType: "EKYC" })}
                            />
                            E-KYC
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                checked={form.txnType === "2FA"}
                                onChange={() => setForm({ ...form, txnType: "2FA" })}
                            />
                            2-Factor Auth
                        </label>
                    </div>

                    <div className="space-y-4">

                        <div>
                            <label className="block mb-2 text-base font-semibold">Aadhaar Number</label>
                            <input
                                name="aadhaar"
                                value={form.aadhaar}
                                onChange={handleChange}
                                maxLength={12}
                                inputMode="numeric"
                                placeholder="12-digit Aadhaar Number"
                                className="w-full h-12 border-2 rounded-lg px-4 text-base"
                            />
                            {form.aadhaar && form.aadhaar.length < 12 && (
                                <p className="text-red-500 text-sm mt-1">Aadhaar must be 12 digits</p>
                            )}
                        </div>

                        <div>
                            <label className="block mb-2 text-base font-semibold">Mobile Number</label>
                            <input
                                name="mobile"
                                value={form.mobile}
                                onChange={handleChange}
                                maxLength={10}
                                inputMode="numeric"
                                placeholder="10-digit Mobile Number"
                                className="w-full h-12 border-2 rounded-lg px-4 text-base"
                            />
                            {form.mobile && form.mobile.length < 10 && (
                                <p className="text-red-500 text-sm mt-1">Mobile must be 10 digits</p>
                            )}
                        </div>

                        <div>
                            <label className="block mb-2 text-base font-semibold">
                                Transaction Amount
                            </label>
                            <input
                                name="amount"
                                value={form.amount}
                                onChange={handleChange}
                                inputMode="numeric"
                                placeholder="Enter Amount"
                                className="w-full h-12 border-2 rounded-lg px-4 text-base"
                            />
                        </div>

                    </div>

                    <div className="mt-5">
                        <div className="grid grid-cols-2 gap-3">

                            <button
                                onClick={() => onSubmit(form)}
                                disabled={
                                    loading ||
                                    processing ||
                                    !isFormValid ||
                                    !is2FAVerified ||
                                    showEkycRequired
                                }
                                className="h-12 bg-green-600 hover:bg-green-700 text-white rounded-lg text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                <Fingerprint size={20} />
                                {loading || processing ? "Processing..." : "Capture & Pay"}
                            </button>

                            <button
                                type="button"
                                onClick={handlePreviousData}
                                disabled={!previousData}
                                className="h-12 bg-slate-700 text-white rounded-lg text-base font-bold disabled:opacity-50"
                            >
                                Previous
                            </button>

                        </div>

                        {!is2FAVerified && (
                            <p className="text-red-500 text-sm mt-3">
                                Please complete daily 2FA first
                            </p>
                        )}
                    </div>
                </div>

                {/* RIGHT — Bank selection */}
                <div className="col-span-7 px-4 py-4 overflow-hidden">

                    <h3 className="font-bold text-xl mb-4">Bank Name :</h3>

                    <div className="grid grid-cols-5 gap-3">
                        {highlightedBanks.map(({ label, bank }, index) => (
                            <button
                                key={bank.iin}
                                type="button"
                                onClick={() => handleQuickBankSelect(bank)}
                                className={`
                                    h-16 rounded-xl text-white font-bold text-sm shadow leading-tight
                                    ${BANK_COLORS[index]}
                                    ${form.bank?.iin === bank.iin ? "ring-4 ring-green-300" : ""}
                                `}
                            >
                                <div>{label}</div>
                                <div className="text-[10px] mt-1 opacity-90">{bank.name}</div>
                            </button>
                        ))}

                        <button
                            onClick={() => setShowOtherBanks(!showOtherBanks)}
                            className="h-16 rounded-xl bg-blue-900 text-white font-bold text-sm shadow"
                        >
                            OTHER<br />BANKS
                        </button>
                    </div>

                    {showOtherBanks && (
                        <div className="mt-4">
                            <select
                                name="bank"
                                value={form.bank?.iin || ""}
                                onChange={handleChange}
                                className="w-full h-12 border-2 rounded-lg px-4 text-base"
                            >
                                <option value="">Select Other Bank</option>
                                {otherBanks.map((b) => (
                                    <option key={b.iin} value={b.iin}>{b.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
